import { forwardRef, useImperativeHandle, useState } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import { Bold, Italic, Underline, List, ListOrdered, Link, RemoveFormatting } from "lucide-react";
import { Button, Input, Label } from "@/components/ui";
import { contractEditorExtensions } from "./contract-editor-security";
import { isSafeContractHref, renderContractHtml, sanitizeContractHtml } from "../patients/contract-html";
import "./contract-rich-text.css";

export interface ContractEditorHandle {
  insertField: (text: string) => void;
}

export const ContractTextEditor = forwardRef<ContractEditorHandle, {
  initialHtml: string;
  onChange: (html: string) => void;
  onBlur: () => void;
}>(function ContractTextEditor({ initialHtml, onChange, onBlur }, ref) {
  const [linkOpen, setLinkOpen] = useState(false);
  const [href, setHref] = useState("");
  const [linkError, setLinkError] = useState("");
  const editor = useEditor({
    extensions: contractEditorExtensions,
    content: renderContractHtml(initialHtml),
    shouldRerenderOnTransaction: true,
    editorProps: {
      attributes: { class: "contract-rich-text min-h-64 p-4 outline-none", role: "textbox", "aria-label": "Texto do contrato", "aria-multiline": "true" },
      transformPastedHTML: renderContractHtml,
    },
    onUpdate: ({ editor: current }) => onChange(sanitizeContractHtml(current.getHTML())),
    onBlur,
  });
  useImperativeHandle(ref, () => ({
    insertField(text) {
      editor?.chain().focus().insertContent({ type: "text", text }).run();
    },
  }), [editor]);

  if (!editor) return null;
  const actions = [
    { label: "Negrito", Icon: Bold, active: editor.isActive("bold"), run: () => editor.chain().focus().toggleBold().run() },
    { label: "Itálico", Icon: Italic, active: editor.isActive("italic"), run: () => editor.chain().focus().toggleItalic().run() },
    { label: "Sublinhado", Icon: Underline, active: editor.isActive("underline"), run: () => editor.chain().focus().toggleUnderline().run() },
    { label: "Lista numerada", Icon: ListOrdered, active: editor.isActive("orderedList"), run: () => editor.chain().focus().toggleOrderedList().run() },
    { label: "Lista com marcadores", Icon: List, active: editor.isActive("bulletList"), run: () => editor.chain().focus().toggleBulletList().run() },
  ];

  return <div className="overflow-hidden rounded-xl border border-app-border bg-app-surface">
    <div role="group" aria-label="Formatação do contrato" className="flex flex-wrap items-center gap-1 border-b border-app-border p-2">
      <select aria-label="Estilo do texto" className="h-9 rounded-md border border-app-border bg-app-surface px-2 text-sm" value={editor.isActive("heading", { level: 1 }) ? "1" : editor.isActive("heading", { level: 2 }) ? "2" : "0"} onChange={event => {
        if (event.target.value === "1" || event.target.value === "2") editor.chain().focus().setHeading({ level: event.target.value === "1" ? 1 : 2 }).run();
        else editor.chain().focus().setParagraph().run();
      }}><option value="0">Texto normal</option><option value="1">Título</option><option value="2">Subtítulo</option></select>
      {actions.map(({ label, Icon, active, run }) => <Button key={label} type="button" variant={active ? "secondary" : "ghost"} size="icon" aria-label={label} title={label} aria-pressed={active} onMouseDown={event => event.preventDefault()} onClick={run}><Icon size={16} aria-hidden="true" /></Button>)}
      <Button type="button" variant="ghost" size="icon" aria-label="Inserir link" title="Inserir link" onMouseDown={event => event.preventDefault()} onClick={() => { setHref(String(editor.getAttributes("link").href || "")); setLinkError(""); setLinkOpen(value => !value); }}><Link size={16} aria-hidden="true" /></Button>
      <Button type="button" variant="ghost" size="icon" aria-label="Limpar formatação" title="Limpar formatação" onMouseDown={event => event.preventDefault()} onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}><RemoveFormatting size={16} aria-hidden="true" /></Button>
    </div>
    {linkOpen && <div className="space-y-2 border-b border-app-border p-3">
      <Label>Endereço do link<Input aria-label="Endereço do link" value={href} onChange={event => setHref(event.target.value)} aria-invalid={!!linkError} /></Label>
      {linkError && <p role="alert" className="text-sm text-app-danger">{linkError}</p>}
      <div className="flex gap-2"><Button type="button" size="sm" onClick={() => {
        if (!isSafeContractHref(href)) { setLinkError("Informe um link http, https, e-mail, telefone ou caminho interno válido."); return; }
        editor.chain().focus().extendMarkRange("link").setLink({ href: href.trim() }).run();
        setLinkOpen(false);
      }}>Aplicar link</Button><Button type="button" variant="ghost" size="sm" onClick={() => { editor.chain().focus().extendMarkRange("link").unsetLink().run(); setLinkOpen(false); }}>Remover link</Button></div>
    </div>}
    <EditorContent editor={editor} />
  </div>;
});
